// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/hr/EmployeeBalancesList.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { Plus, Eye, User, RefreshCw, X, Search, Edit3 } from "lucide-react";
import { toast } from "react-hot-toast"; 
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import PageHeader from "../ui/PageHeader";
import ModernTable from "../ui/ModernTable";
import SelectDropdown from "../../customStyles/SelectDropdown";


const EmployeeBalancesList = () => {
  // --- State Management ---
  const [employees, setEmployees] = useState([]); 
  const [policies, setPolicies] = useState([]);   
  const [activeBalances, setActiveBalances] = useState([]); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditBalanceOpen, setIsEditBalanceOpen] = useState(false); 
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // --- Form State (For initializing a new balance) ---
  const [formData, setFormData] = useState({
    userId: "",
    year: new Date().getFullYear(),
    leavePolicyId: ""
  });

  // --- Edit Balance State ---
  const [editBalancesData, setEditBalancesData] = useState([]); 

  const [formErrors, setFormErrors] = useState({});

  // Dynamic Year selection configuration (பழைய ஆண்டுகள் மறையாமல் இருக்க 2025-இல் இருந்து கணக்கிடப்படுகிறது)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const baseYear = 2025; // பழைய தரவுகள் மறையாமல் இருக்க அடிப்படை ஆண்டு
    const options = [];
    for (let y = baseYear; y <= currentYear + 2; y++) {
      options.push({ label: String(y), id: y });
    }
    return options;
  }, []);

  // Map policies to dropdown options
  const policyOptions = policies.map(p => ({
    label: p.name,
    id: p._id
  }));

  // Map employees to dropdown options
  const employeeOptions = employees.map(emp => ({
    label: `${emp.firstName} ${emp.lastName} (${emp.userId || "No ID"})`,
    id: emp._id
  }));

  // ── FETCH EMPLOYEES ────────────────────────────────────────────────────────
  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const url = SummaryApi.getAllEmployees?.url || "/api/hr/employees";
      const method = SummaryApi.getAllEmployees?.method || "get";
      const res = await api({ url, method });
      if (res.data.success) {
        setEmployees(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── FETCH AVAILABLE LEAVE POLICIES ─────────────────────────────────────────
  const fetchPolicies = async () => {
    try {
      const url = SummaryApi.getAllLeavePolicies?.url || "/api/hr/leave-policies";
      const method = SummaryApi.getAllLeavePolicies?.method || "get";
      const res = await api({ url, method });
      if (res.data.success) {
        setPolicies(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading policies:", err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchPolicies();
  }, []);

  // Extract unique departments dynamically
  const departmentOptions = useMemo(() => {
    const depts = [{ label: "All Departments", id: "all" }];
    const seen = new Set();
    employees.forEach(emp => {
      const dept = emp.departmentId;
      if (dept && dept._id && !seen.has(dept._id)) {
        seen.add(dept._id);
        depts.push({ label: dept.name, id: dept._id });
      }
    });
    return depts;
  }, [employees]);

  // Filter employees locally
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const fullName = `${emp.firstName || ""} ${emp.lastName || ""}`.toLowerCase();
      const matchesSearch = 
        fullName.includes(searchQuery.toLowerCase()) || 
        (emp.userId && emp.userId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (emp.email && emp.email.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDept = 
        selectedDept === "all" || 
        (emp.departmentId && emp.departmentId._id === selectedDept);

      return matchesSearch && matchesDept;
    });
  }, [employees, searchQuery, selectedDept]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDept]);

  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredEmployees.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredEmployees, currentPage, itemsPerPage]);

  const fetchUserBalances = async (userId) => {
    try {
      const url = SummaryApi.getUserLeaveBalance?.url(userId) || `/api/hr/leave-balance/${userId}`;
      const method = SummaryApi.getUserLeaveBalance?.method || "get";
      
      const res = await api({ 
        url: `${url}?year=${selectedYear}`, 
        method 
      });

      if (res.data.success && res.data.data) {
        return res.data.data.balances || [];
      }
      return [];
    } catch (err) {
      console.error("Failed to load leave balance sheet.", err);
      return [];
    }
  };

  const handleTriggerAccrual = async () => {
    if (!window.confirm("Are you sure you want to trigger manual monthly accrual for all active employees?")) return;
    
    setIsLoading(true);
    try {
      const url = SummaryApi.accrueManual?.url || "/api/hr/leave-balance/accrue-manual";
      const method = SummaryApi.accrueManual?.method || "post";
      
      const res = await api({ url, method });
      if (res.data.success) {
        toast.success(res.data.message || "Accrual engine processed successfully!");
        await fetchEmployees();
      } else {
        toast.error(res.data.message || "Failed to trigger accrual engine.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error processing accrual request.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (field, val) => {
    setFormData(prev => ({ ...prev, [field]: val }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const openFormDrawer = () => {
    setFormData({
      userId: "",
      year: selectedYear,
      leavePolicyId: ""
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  const closeFormDrawer = () => {
    setIsFormOpen(false);
  };

  const openViewDrawer = async (employee) => {
    setSelectedEmployee(employee);
    setIsViewOpen(true);
    const balances = await fetchUserBalances(employee._id);
    setActiveBalances(balances);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedEmployee(null);
    setActiveBalances([]);
  };

  const openEditBalanceDrawer = async (employee) => {
    setSelectedEmployee(employee);
    const balances = await fetchUserBalances(employee._id);
    
    const mapped = balances.map(b => ({
      leaveTypeId: b.leaveTypeId?._id || b.leaveTypeId,
      code: b.leaveTypeId?.code || "LV",
      name: b.leaveTypeId?.name || "Leave",
      color: b.leaveTypeId?.color || "#6366f1",
      allocated: b.allocated || 0,
      carriedForward: b.carriedForward || 0
    }));

    setEditBalancesData(mapped);
    setIsEditBalanceOpen(true);
  };

  const closeEditBalanceDrawer = () => {
    setIsEditBalanceOpen(false);
    setSelectedEmployee(null);
    setEditBalancesData([]);
  };

  const handleEditBalanceChange = (index, field, value) => {
    const updated = [...editBalancesData];
    updated[index] = { ...updated[index], [field]: Number(value) };
    setEditBalancesData(updated);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.userId) errors.userId = "Please select an employee";
    if (!formData.leavePolicyId) errors.leavePolicyId = "Please select a leave policy";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const url = SummaryApi.initLeaveBalance?.url || "/api/hr/leave-balance/init";
      const method = SummaryApi.initLeaveBalance?.method || "post";

      const res = await api({
        url,
        method,
        data: formData
      });

      if (res.data.success) {
        toast.success(res.data.message || "Leave balance initialized successfully!");
        await fetchEmployees();
        closeFormDrawer();
      } else {
        toast.error(res.data.message || "Failed to initialize balance.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error initializing leave balance.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditBalanceSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      for (const item of editBalancesData) {
        const url = SummaryApi.updateLeaveBalance?.url(selectedEmployee._id) || `/api/hr/leave-balance/${selectedEmployee._id}/override`;
        const method = SummaryApi.updateLeaveBalance?.method || "put";

        await api({
          url,
          method,
          data: {
            year: selectedYear,
            leaveTypeId: item.leaveTypeId,
            allocated: item.allocated,
            carriedForward: item.carriedForward
          }
        });
      }

      toast.success("Leave balance overridden successfully!");
      closeEditBalanceDrawer();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error overriding leave balances.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Table Columns Mapping ---
  const columns = [
    {
      key: "name",
      label: "Employee Name",
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-800">{`${row.firstName} ${row.lastName}`}</span>
          <span className="text-xs text-gray-400">{row.email}</span>
        </div>
      ),
    },
    {
      key: "userId",
      label: "Employee ID",
      render: (row) => <span className="font-mono text-xs font-bold text-gray-500">{row.userId || "Not Assigned"}</span>,
    },
    {
      key: "departmentId",
      label: "Department",
      render: (row) => <span className="text-gray-600 font-semibold text-xs">{row.departmentId?.name || "Unassigned"}</span>,
    },
    {
      key: "leavePolicyId",
      label: "Assigned Policy",
      render: (row) => (
        <span className="px-2.5 py-1 rounded bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100">
          {row.leavePolicyId?.name || "No Policy Associated"}
        </span>
      ),
    },
    {
      key: "employmentStatus",
      label: "Status",
      render: (row) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${row.employmentStatus === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${row.employmentStatus === "active" ? "bg-green-500" : "bg-red-500"}`} />
          {row.employmentStatus === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const tableActions = [
    {
      label: "View Balance Sheet",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    },
    {
      label: "Edit Balance",
      icon: <Edit3 className="w-4 h-4 text-amber-600" />,
      onClick: (row) => openEditBalanceDrawer(row),
    },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="Employee Balances"
        description="Monitor individual leaf accounts, configure annual allowances, and execute dynamic accrual cycles."
        titleIcon={User}
        action={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleTriggerAccrual}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg font-bold text-sm hover:bg-gray-50 shadow-sm"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              <span>Trigger Monthly Accrual</span>
            </button>

            <button
              type="button"
              onClick={openFormDrawer}
              className="flex items-center bg-primary hover:bg-secondary justify-center gap-2 px-5 py-2.5 rounded-lg font-bold text-white text-sm hover:opacity-90 shadow-sm transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Initialize Balance</span>
            </button>
          </div>
        }
      />

      <div className="mt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-end">
          <div className="w-full">
            <label className="block text-sm font-semibold mb-1.5 text-gray-700">Search Employee</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Name, ID or Email..."
                className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-100"
              />
            </div>
          </div>

          <div className="w-full">
            <SelectDropdown
              label="Filter by Department"
              options={departmentOptions}
              value={selectedDept}
              onChange={(val) => setSelectedDept(val)}
              labelKey="label"
              valueKey="id"
            />
          </div>

          <div className="w-full">
            <SelectDropdown
              label="Selected Fiscal Year"
              options={yearOptions}
              value={selectedYear}
              onChange={(val) => setSelectedYear(val)}
              labelKey="label"
              valueKey="id"
            />
          </div>
        </div>

        <ModernTable
          columns={columns}
          data={paginatedEmployees}
          actions={tableActions}
          selectable={false}
          actionType="dropdown"
          pagination={{
            totalItems: filteredEmployees.length,
            itemsPerPage: itemsPerPage,
            currentPage: currentPage,
            onPageChange: (page) => setCurrentPage(page),
            onItemsPerPageChange: (limit) => {
              setItemsPerPage(limit);
              setCurrentPage(1);
            }
          }}
        />
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE READ-ONLY LEAVE BREAKDOWN SHEET */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Leave Balance Sheet
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Real-time breakdown for Fiscal Year {selectedYear}</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-1">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Employee Account</div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : ""}
                </div>
                <div className="text-xs font-semibold text-gray-500">
                  Policy: <strong className="text-primary">{selectedEmployee?.leavePolicyId?.name || "N/A"}</strong>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Types Breakdown</h4>
                
                {activeBalances.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-gray-200 rounded-2xl text-gray-400 text-xs">
                    No active balances discovered. Please initialize leave configuration for this employee.
                  </div>
                ) : (
                  activeBalances.map((bal, idx) => {
                    const totalAllocated = (bal.allocated || 0) + (bal.carriedForward || 0);
                    const remaining = totalAllocated - (bal.used || 0) - (bal.pending || 0);
                    
                    return (
                      <div key={idx} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider"
                            style={{
                              backgroundColor: `${bal.leaveTypeId?.color || "#6366f1"}15`,
                              color: bal.leaveTypeId?.color || "#6366f1",
                              border: `1px solid ${bal.leaveTypeId?.color || "#6366f1"}30`
                            }}
                          >
                            {bal.leaveTypeId?.code || "LV"}
                          </span>
                          <span className="text-sm font-bold text-gray-800">
                            {bal.leaveTypeId?.name}
                          </span>
                        </div>

                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                          <div className="p-1 bg-slate-50 rounded">
                            <div className="text-gray-400 text-[10px] uppercase font-bold">Allocated</div>
                            <div className="font-bold text-gray-700 mt-1">{bal.allocated}</div>
                          </div>
                          <div className="p-1 bg-slate-50 rounded">
                            <div className="text-gray-400 text-[10px] uppercase font-bold">Carried</div>
                            <div className="font-bold text-gray-700 mt-1">{bal.carriedForward}</div>
                          </div>
                          <div className="p-1 bg-slate-50 rounded">
                            <div className="text-gray-400 text-[10px] uppercase font-bold">Used</div>
                            <div className="font-bold text-red-600 mt-1">{bal.used}</div>
                          </div>
                          <div className="p-1 bg-slate-50 rounded">
                            <div className="text-gray-400 text-[10px] uppercase font-bold">Pending</div>
                            <div className="font-bold text-yellow-600 mt-1">{bal.pending}</div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-1 border-t border-gray-50 text-xs font-bold">
                          <span className="text-gray-500">Net Remaining Balance:</span>
                          <span className="text-sm font-bold text-emerald-600">{remaining.toFixed(1)} Day(s)</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 text-white text-sm font-bold rounded-lg transition-colors bg-primary hover:bg-secondary"
              >
                Close Sheet
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE EDIT / OVERRIDE BALANCES DRAWER */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isEditBalanceOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeEditBalanceDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isEditBalanceOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Override Leave Balances
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Manually adjust allocations for Fiscal Year {selectedYear}</p>
              </div>
              <button
                type="button"
                onClick={closeEditBalanceDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Body */}
            <form onSubmit={handleEditBalanceSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-1">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Employee Account</div>
                <div className="text-lg font-bold text-gray-800">
                  {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : ""}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Balance Overrides</h4>
                {editBalancesData.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-gray-200 rounded-2xl text-gray-400 text-xs">
                    Please initialize leave balance for this employee before attempting overrides.
                  </div>
                ) : (
                  editBalancesData.map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 shadow-sm">
                      <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider"
                          style={{
                            backgroundColor: `${item.color}15`,
                            color: item.color,
                            border: `1px solid ${item.color}30`
                          }}
                        >
                          {item.code}
                        </span>
                        <span className="text-sm font-bold text-gray-800">{item.name}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Allocated Days</label>
                          <input
                            type="number"
                            value={item.allocated}
                            onChange={(e) => handleEditBalanceChange(index, "allocated", e.target.value)}
                            className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Carried Days</label>
                          <input
                            type="number"
                            value={item.carriedForward}
                            onChange={(e) => handleEditBalanceChange(index, "carriedForward", e.target.value)}
                            className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </form>

            {/* Drawer Footer buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeEditBalanceDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleEditBalanceSubmit}
                disabled={isSubmitting || editBalancesData.length === 0}
                className="px-5 py-2.5 bg-primary hover:bg-secondary rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-50"
              >
                {isSubmitting ? "Overriding..." : "Save Overrides"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE INITIALIZE BALANCE FORM DRAWER */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isFormOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeFormDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isFormOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Initialize Account Balance
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Assign policy allocations to configure starting balances.</p>
              </div>
              <button
                type="button"
                onClick={closeFormDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Scrollable Body */}
            <form onSubmit={handleFormSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* Select Employee */}
              <div>
                <SelectDropdown
                  label="Select Target Employee *"
                  options={employeeOptions}
                  value={formData.userId}
                  onChange={(val) => handleSelectChange("userId", val)}
                  labelKey="label"
                  valueKey="id"
                  searchable={true}
                  placeholder="Choose an employee"
                  zIndex={66}
                />
                {formErrors.userId && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.userId}</p>}
              </div>

              {/* Select Policy */}
              <div>
                <SelectDropdown
                  label="Select Allocation Policy *"
                  options={policyOptions}
                  value={formData.leavePolicyId}
                  onChange={(val) => handleSelectChange("leavePolicyId", val)}
                  labelKey="label"
                  valueKey="id"
                  placeholder="Choose policy tier"
                  zIndex={66}
                />
                {formErrors.leavePolicyId && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.leavePolicyId}</p>}
              </div>

              {/* Select Year */}
              <div>
                <SelectDropdown
                  label="Fiscal Year Allocation *"
                  options={yearOptions}
                  value={formData.year}
                  onChange={(val) => handleSelectChange("year", val)}
                  labelKey="label"
                  valueKey="id"
                  zIndex={66}
                />
              </div>
            </form>

            {/* Footer buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeFormDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-colors bg-primary hover:bg-secondary"
              >
                {isSubmitting ? "Initializing Sheet..." : "Initialize Balance"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeBalancesList;